import Grid from "@bmi-digital/components/grid";
import MediaGallery from "@bmi-digital/components/media-gallery";
import Section from "@bmi-digital/components/section";
import Thumbnail from "@bmi-digital/components/thumbnail";
import { microCopy } from "@bmi-digital/dxb-microcopies";
import { graphql } from "gatsby";
import React from "react";
import { isDefined } from "@bmi/utils";
import { useTheme } from "@mui/material/styles";
import BrandLogo from "../../components/BrandLogo";
import Breadcrumbs from "../../components/Breadcrumbs";
import { generateGetMicroCopy } from "../../components/MicroCopy";
import Page from "../../components/Page";
import RelatedSystems from "../../components/RelatedSystems";
import ShareWidgetSection, {
  ShareWidgetSectionData
} from "../../components/ShareWidgetSection";
import withGTM from "../../utils/google-tag-manager";
import { transformMediaSrc } from "../../utils/media";
import { transformImages } from "../../utils/product-details-transforms";
import { PayloadSpecificationNotesWithCta } from "../../types/payload";
import LeadBlockSection from "./leadBlockSection";
import { StyledImageGalarySection } from "./styles/systemDetailsPage.styles";
import SystemLayersSection from "./systemLayersSection";
import TabLeadBlock from "./tabLeadBlock";
import type { HomePageData, SiteData } from "../../components/Site";
import type { MicroCopy as MicroCopyData } from "../../components/MicroCopy";
import type { ThumbnailProps } from "@bmi-digital/components/thumbnail";
import type { System } from "../../types/pim";
import type { CalculatorConfig } from "../../components/pitched-roof-calculator/types";
import type { HouseType } from "../../components/visualiser/Types";
import type { GeneralErrorData } from "../general-error";
import type { CountryNavigation, SampleOrder } from "../../components/Header";
import type { Visualiser } from "../../components/Visualiser";
import type { InternalLinkBlock, LinkData } from "../../components/link/types";
import type { TitleWithContentData } from "../../components/TitleWithContent";
import type { RichTextData } from "../../components/RichText";

export type SpecificationNotesWithCta = Pick<
  PayloadSpecificationNotesWithCta,
  "title"
> & {
  description: RichTextData | null;
  cta: readonly [LinkData] | null;
};

export type SystemDetailsPageData = {
  sdpShareWidget: readonly [ShareWidgetSectionData];
  sdpLeadBlockCta: readonly [InternalLinkBlock] | null;
  sdpSidebarItems: readonly [TitleWithContentData] | null;
  sdpBimDescription: RichTextData | null;
  sdpSpecificationNotesWithCta: readonly [SpecificationNotesWithCta] | null;
};

type Props = {
  pageContext: {
    systemCode: string;
  };
  data: {
    system: System;
    payloadSystemDetailsPage: SystemDetailsPageData | null;
    payloadSite: SiteData;
    allPayloadMicroCopy: {
      nodes: MicroCopyData[];
    };
    payloadPitchedRoofCalculatorConfig: CalculatorConfig | null;
    allPayloadHouseModel: {
      nodes: HouseType[];
    };
    payloadHomePage: HomePageData;
    payloadGeneralError: GeneralErrorData;
    payloadCountryNavigation: CountryNavigation;
    payloadSampleOrder: SampleOrder | null;
    payloadVisualiser: Visualiser | null;
  };
};

const SystemDetailsPage = ({ pageContext, data }: Props) => {
  const { system, payloadSystemDetailsPage } = data;
  const theme = useTheme();

  const getMicroCopy = generateGetMicroCopy(data.allPayloadMicroCopy.nodes);

  const BreadcrumbSection = ({ location }: { location: "top" | "bottom" }) => (
    <Section
      backgroundColor="pearl"
      isSlim
      data-testid={`system-details-page-breadcrumbs-section${location}`}
    >
      <Breadcrumbs
        data={[
          {
            id: pageContext.systemCode,
            label: system.name,
            slug: null
          }
        ]}
        data-testid={`system-details-page-breadcrumbs-${location}`}
      />
    </Section>
  );

  const images = system.masterImage
    ? transformImages([system.masterImage, ...system.galleryImages])
    : transformImages([...system.galleryImages]);

  const videos = transformMediaSrc(system.videos, theme).filter(isDefined);

  //TODO: check if it doesnt work on system detals page!!!
  const media = [...images, ...videos];

  const GTMThumbnail = withGTM<ThumbnailProps>(Thumbnail, {
    label: "media",
    action: "media"
  });

  return (
    <Page
      brand={system.brand?.code}
      title={system.name}
      pageData={{ breadcrumbs: null, signupBlock: null, meta: null, path: "" }}
      siteData={data.payloadSite}
      microCopy={data.allPayloadMicroCopy.nodes}
      pitchedRoofCalculatorConfig={data.payloadPitchedRoofCalculatorConfig}
      visualiserHouseTypes={data.allPayloadHouseModel.nodes}
      homePage={data.payloadHomePage}
      generalError={data.payloadGeneralError}
      countryNavigation={data.payloadCountryNavigation}
      sampleOrder={data.payloadSampleOrder}
      visualiser={data.payloadVisualiser}
    >
      <BreadcrumbSection location="top" />
      {payloadSystemDetailsPage?.sdpShareWidget[0] ? (
        <ShareWidgetSection data={payloadSystemDetailsPage.sdpShareWidget[0]} />
      ) : undefined}
      <LeadBlockSection
        name={system.name}
        cta={payloadSystemDetailsPage.sdpLeadBlockCta?.[0] ?? undefined}
        promotionalContent={system.promotionalContent ?? undefined}
        uniqueSellingPropositions={system.uniqueSellingPropositions}
        brandLogo={
          system.brand?.code ? (
            <BrandLogo brandName={system.brand?.code} />
          ) : undefined
        }
        goodBetterBest={system.goodBetterBest}
      />
      <StyledImageGalarySection
        backgroundColor="alabaster"
        data-testid="system-details-page-image-gallary-section"
      >
        <Grid container spacing={3}>
          <Grid xs={12} md={12} lg={8}>
            <MediaGallery
              media={media}
              layout="short"
              thumbnailComponent={(props: ThumbnailProps) => (
                <GTMThumbnail gtm={{ id: "media-gallery1" }} {...props} />
              )}
              videoButtonLabel={getMicroCopy(microCopy.MEDIA_VIDEO)}
            />
          </Grid>
          {system && system.systemLayers && system.systemLayers.length > 0 && (
            <Grid xs={12} md={12} lg={4}>
              <SystemLayersSection systemLayers={system.systemLayers} />
            </Grid>
          )}
        </Grid>
      </StyledImageGalarySection>
      <TabLeadBlock
        system={system}
        aboutLeadBlockGenericContent={
          payloadSystemDetailsPage.sdpSidebarItems?.[0]
        }
        bimDescription={payloadSystemDetailsPage.sdpBimDescription ?? undefined}
        specificationNotes={
          payloadSystemDetailsPage.sdpSpecificationNotesWithCta?.[0]
        }
      />
      {system.relatedSystems.length > 0 && (
        <RelatedSystems systems={system.relatedSystems} />
      )}
      <BreadcrumbSection location="bottom" />
    </Page>
  );
};

export default SystemDetailsPage;

export const systemQuery = graphql`
  query SystemDetailsPage($systemCode: String!) {
    system(code: { eq: $systemCode }) {
      awardsAndCertificateDocuments {
        ...PIMAssetFragment
      }
      awardsAndCertificateImages {
        ...PIMAssetFragment
      }
      bim {
        name
        url
      }
      brand {
        ...PIMBrandFragment
      }
      categories {
        ...CategoryFragment
      }
      classifications {
        ...ClassificationFragment
      }
      code
      description
      documents {
        ...PIMSystemDocumentFragment
      }
      goodBetterBest
      guaranteesAndWarrantiesImages {
        ...PIMAssetFragment
      }
      guaranteesAndWarrantiesLinks {
        ...PIMAssetFragment
      }
      keyFeatures {
        name
        values
      }
      galleryImages {
        ...PIMImageFragment
      }
      masterImage {
        ...PIMImageFragment
      }
      layerCodes
      name
      promotionalContent
      relatedSystems {
        ...RelatedSystemFragment
      }
      specification {
        ...PIMAssetFragment
      }
      systemBenefits
      systemLayers {
        layerNumber
        name
        relatedProducts {
          name
          path
        }
        relatedOptionalProducts {
          name
          path
        }
        shortDescription
        type
      }
      uniqueSellingPropositions
      videos {
        ...PIMVideoFragment
      }
    }
    payloadSystemDetailsPage {
      ... on PayloadSystemDetailsPage {
        sdpShareWidget {
          ...ShareWidgetFragment
        }
        sdpLeadBlockCta {
          ...InternalLinkBlockFragment
        }
        sdpSidebarItems {
          ...TitleWithContentFragment
        }
        sdpBimDescription {
          ...RichTextFragment
        }
        sdpSpecificationNotesWithCta {
          ... on PayloadSpecificationNotesWithCta {
            title
            description {
              ...RichTextFragment
            }
            cta {
              ...LinkFragment
            }
          }
        }
      }
    }
    payloadSite {
      ...SiteFragment
    }
    allPayloadMicroCopy {
      nodes {
        ...MicroCopyFragment
      }
    }
    payloadPitchedRoofCalculatorConfig {
      ...PitchedRoofCalculatorFragment
    }
    allPayloadHouseModel {
      nodes {
        ...VisualiserHouseFragment
      }
    }
    payloadHomePage {
      ...HomePageFragment
    }
    payloadGeneralError {
      ...GeneralErrorFragment
    }
    payloadCountryNavigation {
      ...CountryNavigationFragment
    }
    payloadSampleOrder {
      ...SampleOrderFragment
    }
    payloadVisualiser {
      ...VisualiserFragment
    }
  }
`;
